/**
 * Centralized Texture Management System
 *
 * Handles all texture loading, caching, and hot-reloading for the game.
 * Provides a single point of control for texture assets.
 */

import { Assets, type Texture } from 'pixi.js';

// Types
type TextureCategory =
  | 'pawns'
  | 'animals'
  | 'tiles'
  | 'objects'
  | 'items'
  | 'corpses'
  | 'projectiles';
type ReloadCallback = () => void | Promise<void>;

interface TextureEntry {
  readonly texture: Texture;
  readonly loadedAt: number;
}

// State
const textureCache = new Map<string, TextureEntry>();
const reloadCallbacks = new Map<TextureCategory, Set<ReloadCallback>>();
const categoryCallbacks = new Map<string, Set<ReloadCallback>>(); // For specific paths

// Dev mode detection and cache busting
const isDev = import.meta.env.DEV;
let globalCacheBuster = isDev ? Date.now() : 0;

/**
 * Get the full URL for a texture path with cache busting in dev mode
 */
const getTextureUrl = ({ path }: { path: string }): string => {
  if (!isDev) {
    return path;
  }
  return `${path}?t=${globalCacheBuster}`;
};

/**
 * Extract category from a texture path
 * e.g., "/pawns/front_idle.png" -> "pawns"
 */
const getCategoryFromPath = ({ path }: { path: string }): TextureCategory | null => {
  const match = path.match(/^\/?(pawns|animals|tiles|objects|items|corpses|projectiles)\//);
  if (match?.[1]) {
    return match[1] as TextureCategory;
  }
  return null;
};

/**
 * Load a texture with caching and cache-busting support
 */
export const loadTexture = async ({ path }: { path: string }): Promise<Texture> => {
  const url = getTextureUrl({ path });

  // Check cache first (in production, or if same cache buster)
  const cached = textureCache.get(path);
  if (cached && (!isDev || cached.loadedAt === globalCacheBuster)) {
    return cached.texture;
  }

  // Unload old version if exists
  if (isDev && cached) {
    try {
      await Assets.unload(path);
    } catch {
      // Ignore unload errors
    }
  }

  // Load fresh texture
  const texture = await Assets.load(url);

  // Cache it
  textureCache.set(path, {
    texture,
    loadedAt: globalCacheBuster,
  });

  return texture;
};

/**
 * Load multiple textures in parallel
 */
export const loadTextures = async ({
  paths,
}: {
  paths: readonly string[];
}): Promise<Map<string, Texture>> => {
  const results = await Promise.all(
    paths.map(async (path) => {
      const texture = await loadTexture({ path });
      return { path, texture };
    })
  );

  const textureMap = new Map<string, Texture>();
  for (const { path, texture } of results) {
    textureMap.set(path, texture);
  }
  return textureMap;
};

/**
 * Preload all textures in a category
 */
export const preloadCategory = async ({
  category,
  paths,
}: {
  category: TextureCategory;
  paths: readonly string[];
}): Promise<void> => {
  const fullPaths = paths.map((p) => `/${category}/${p}`);
  await loadTextures({ paths: fullPaths });
};

/**
 * Register a callback to be called when textures in a category are reloaded
 */
export const onCategoryReload = ({
  category,
  callback,
}: {
  category: TextureCategory;
  callback: ReloadCallback;
}): (() => void) => {
  let callbacks = reloadCallbacks.get(category);
  if (!callbacks) {
    callbacks = new Set();
    reloadCallbacks.set(category, callbacks);
  }
  callbacks.add(callback);

  // Return unsubscribe function
  return () => {
    callbacks?.delete(callback);
  };
};

/**
 * Register a callback for a specific path pattern
 */
export const onPathReload = ({
  pathPrefix,
  callback,
}: {
  pathPrefix: string;
  callback: ReloadCallback;
}): (() => void) => {
  let callbacks = categoryCallbacks.get(pathPrefix);
  if (!callbacks) {
    callbacks = new Set();
    categoryCallbacks.set(pathPrefix, callbacks);
  }
  callbacks.add(callback);

  return () => {
    callbacks?.delete(callback);
  };
};

/**
 * Invalidate cache for a specific path and trigger reload callbacks
 */
export const invalidateTexture = async ({ path }: { path: string }): Promise<void> => {
  // Remove from cache
  textureCache.delete(path);

  // Try to unload from PixiJS
  try {
    await Assets.unload(path);
  } catch {
    // Ignore
  }

  // Find and call relevant callbacks
  const category = getCategoryFromPath({ path });
  if (category) {
    const callbacks = reloadCallbacks.get(category);
    if (callbacks) {
      for (const cb of callbacks) {
        await cb();
      }
    }
  }

  // Check path-specific callbacks
  for (const [prefix, callbacks] of categoryCallbacks) {
    if (path.startsWith(prefix)) {
      for (const cb of callbacks) {
        await cb();
      }
    }
  }
};

/**
 * Invalidate all textures in a category
 */
export const invalidateCategory = async ({
  category,
}: {
  category: TextureCategory;
}): Promise<void> => {
  const prefix = `/${category}/`;

  // Remove matching entries from cache
  for (const path of textureCache.keys()) {
    if (path.startsWith(prefix)) {
      textureCache.delete(path);
      try {
        await Assets.unload(path);
      } catch {
        // Ignore
      }
    }
  }

  // Update cache buster
  globalCacheBuster = Date.now();

  // Call category callbacks
  const callbacks = reloadCallbacks.get(category);
  if (callbacks) {
    for (const cb of callbacks) {
      await cb();
    }
  }
};

/**
 * Invalidate all textures and trigger all reload callbacks
 */
export const invalidateAll = async (): Promise<void> => {
  // Clear cache
  textureCache.clear();

  // Update cache buster
  globalCacheBuster = Date.now();

  // Unload all from PixiJS Assets
  await Assets.reset();

  // Call all callbacks
  for (const callbacks of reloadCallbacks.values()) {
    for (const cb of callbacks) {
      await cb();
    }
  }
  for (const callbacks of categoryCallbacks.values()) {
    for (const cb of callbacks) {
      await cb();
    }
  }
};

/**
 * Handle HMR event from Vite plugin
 */
export const handleAssetChange = async ({ path }: { path: string }): Promise<void> => {
  console.log(`[TextureManager] Asset changed: ${path}`);

  // Update cache buster for fresh loads
  globalCacheBuster = Date.now();

  // Invalidate the specific texture
  await invalidateTexture({ path });
};

/**
 * Get cache statistics (useful for debugging)
 */
export const getCacheStats = (): {
  totalTextures: number;
  byCategory: Record<string, number>;
} => {
  const byCategory: Record<string, number> = {};

  for (const path of textureCache.keys()) {
    const category = getCategoryFromPath({ path }) ?? 'other';
    byCategory[category] = (byCategory[category] ?? 0) + 1;
  }

  return {
    totalTextures: textureCache.size,
    byCategory,
  };
};

// HMR support
if (import.meta.hot) {
  // Expose for debugging
  (window as unknown as { TextureManager: object }).TextureManager = {
    invalidateAll,
    invalidateCategory,
    invalidateTexture,
    getCacheStats,
  };

  // Listen for public asset changes
  import.meta.hot.on('public-asset-change', (data: { path: string }) => {
    handleAssetChange({ path: data.path });
  });
}
